import { getLocalCalendarDayAsUTC, getLocalDateFromUTC } from "../../../../Helper/DatePicker/DateConverter";
import { FEATURE_CODES } from "../../../../Static/FeatureMapping";
import { renderCountBadge } from "../../../AdvancedAnalytics/Common/CommonFunctions";

// Pack replacement markers on the SOH degradation curve (SOH Computed Date axis only).
// Rendered the same way as the Smart Alert / BMS fault markers: an ApexCharts point
// annotation carrying an SVG image, plus a vertical line, with the shared
// setupAnnotationTooltips helper wiring hover / click-to-pin cards.
// Blue keeps it apart from the alert palette (severity red/orange/yellow, resolved
// green, BMS fault teal, smart alert indigo).
export const PACK_REPLACEMENT_DISPLAY = {
    label: "Pack Replacement",
    color: "#1565C0",
    bg: "#E3F0FB",
};

// MUI Battery0Bar turned on its side (terminal right), filled, with a two-arrow sync
// glyph cut out of it. The mask id is safe to repeat because every copy is drawn
// inside its own data-URL image, never inline in the document.
const getPackReplacementGlyph = (color) => `
    <defs>
        <mask id="packReplacementCutout">
            <rect width="24" height="24" fill="#fff"/>
            <g fill="#000" stroke="#000">
                <path d="M8.34 11.29A2.75 2.75 0 0 1 13.25 10.42" fill="none" stroke-width="1.4" stroke-linejoin="round"/>
                <path d="M14.00 11.49L12.29 11.09L14.21 9.75Z"/>
                <path d="M13.66 12.71A2.75 2.75 0 0 1 8.75 13.58" fill="none" stroke-width="1.4" stroke-linejoin="round"/>
                <path d="M8.00 12.51L9.71 12.91L7.79 14.25Z"/>
            </g>
        </mask>
    </defs>
    <path mask="url(#packReplacementCutout)" fill="${color}"
        d="M20 8v8c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V8c0-.55.45-1 1-1h16c.55 0 1 .45 1 1m2 2h-2v4h2z"/>
`;

const toDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// Chart marker: same 30x30 frame, 27px disc and count badge as getIncidentIconSvg.
export const getPackReplacementMarkerUrl = (count) => toDataUrl(`
<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="13.5" cy="13.5" r="13.5" fill="${PACK_REPLACEMENT_DISPLAY.bg}"/>
    <svg x="4.5" y="4.5" width="18" height="18" viewBox="0 0 24 24">${getPackReplacementGlyph(PACK_REPLACEMENT_DISPLAY.color)}</svg>
    ${renderCountBadge(count)}
</svg>`);

// Bare glyph for the tooltip tag (used as an <img>, so the mask id stays isolated).
export const getPackReplacementGlyphUrl = () => toDataUrl(`
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${getPackReplacementGlyph(PACK_REPLACEMENT_DISPLAY.color)}</svg>`);

/**
 * Flatten service records (GET /api/batteries/<id>/service-records) into one entry per
 * replaced pack and group them into one marker per day, anchored on recordedAt.
 * @returns {Array<{ timestamp: number, packs: Array, count: number }>} sorted by day
 */
export const buildPackReplacementTimePoints = (records = []) => {
    const byDay = new Map();
    records.forEach((record) => {
        const details = record?.serviceDetails || {};
        // The user-timezone calendar day, as UTC midnight -- the axis convention.
        const timestamp = getLocalCalendarDayAsUTC(details.recordedAt);
        if (timestamp === null) return;

        const packs = (record.packReplacementDetails || []).map((pack) => ({
            requestID: record.replacementRequestUUID,
            recordedAt: getLocalDateFromUTC(details.recordedAt, "DD MMM'YY h:mm A"),
            recordedByName: details.recordedByName,
            odometerKm: details.odometerKm,
            string: pack.string,
            pack: pack.pack,
            outgoingSerial: pack.outgoingSerial,
            incomingSerial: pack.incomingSerial,
            reasons: (pack.reasons || []).map((reason) => reason?.label || reason?.code || reason).filter(Boolean),
        }));
        if (!packs.length) return;

        if (!byDay.has(timestamp)) byDay.set(timestamp, []);
        byDay.get(timestamp).push(...packs);
    });

    return [...byDay.entries()]
        .sort(([a], [b]) => a - b)
        .map(([timestamp, packs]) => ({ timestamp, packs, count: packs.length }));
};

/**
 * Same gate as the Digital Twin Components tab (DigitalTwinMain.jsx): the org must show
 * the "components" tab, and on RBAC v2 the user needs BATTERY_COMPONENTS view. On v1,
 * hasPermission would be true for everything, so only the tab config decides.
 */
export const canShowPackReplacements = ({ tabsToShow, isRbacV2, hasPermission }) =>
    (tabsToShow || []).includes("components") &&
    (isRbacV2 ? !!hasPermission?.(FEATURE_CODES.BATTERY_COMPONENTS, "view") : true);

/**
 * Vehicle ids for the operational status "Battery pack(s) replaced during this downtime?"
 * cross-check. Empty unless pack replacement is available to this user (the same gate as
 * the Components tab), so the question is not asked of users who cannot record one: the
 * lookup behind it needs BATTERY_COMPONENTS view, and a Yes would only block the update.
 */
export const replacementCheckVehicleIds = ({ vehicleId, tabsToShow, isRbacV2, hasPermission }) =>
    vehicleId && canShowPackReplacements({ tabsToShow, isRbacV2, hasPermission }) ? [vehicleId] : [];

/** Keep only markers ApexCharts will actually draw (it clips points outside the visible x range). */
export const filterTimePointsInRange = (timePoints, minX, maxX) =>
    (timePoints || []).filter(
        (point) => !Number.isFinite(minX) || !Number.isFinite(maxX) || (point.timestamp >= minX && point.timestamp <= maxX)
    );
